import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle, RotateCcw, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabel = (r) => (r || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function DomainMigrationModal({ open, onClose, org, token, onDone }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [currentDomain, setCurrentDomain] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);
  const [rollbackConfirmed, setRollbackConfirmed] = useState(false);
  const [result, setResult] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!org?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate-domain/history/${org.id}`, { headers });
      const data = await res.json();
      if (res.ok) setLatest(data.latest || null);
    } catch (_) { /* non-blocking */ }
  }, [org?.id, token]);

  useEffect(() => {
    if (open) {
      setCurrentDomain(org?.domain || "");
      setNewDomain("");
      setConfirmed(false);
      setPreview(null);
      setResult(null);
      setRollbackConfirmed(false);
      fetchHistory();
    }
  }, [open, org?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreview = async () => {
    if (!currentDomain.trim() || !newDomain.trim()) return toast.error("Enter both domains");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate-domain/preview`, {
        method: "POST", headers,
        body: JSON.stringify({ org_id: org.id, current_domain: currentDomain, new_domain: newDomain }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data);
      if (data.affected_count === 0) toast.warning(`No users with @${data.current_domain} emails found`);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const execute = async () => {
    if (!confirmed) return toast.error("Please check the confirmation box");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate-domain`, {
        method: "POST", headers,
        body: JSON.stringify({ org_id: org.id, current_domain: currentDomain, new_domain: newDomain, confirmed: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Migration failed");
      setResult({ type: "migrate", data });
      toast.success(`Migrated ${data.migrated_count} account(s) to @${data.new_domain}`);
      await fetchHistory();
      onDone && onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const rollback = async () => {
    if (!rollbackConfirmed) return toast.error("Please confirm the rollback");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate-domain/rollback`, {
        method: "POST", headers,
        body: JSON.stringify({ migration_id: latest.id, confirmed: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Rollback failed");
      setResult({ type: "rollback", data });
      toast.success(`Reverted ${data.reverted_count} account(s) to @${data.domain}`);
      await fetchHistory();
      onDone && onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const confirmText = `I confirm that all users with @${currentDomain || "{current_domain}"} emails in this organization should be migrated to @${newDomain || "{new_domain}"}. After migration, only @${newDomain || "{new_domain}"} emails will work for login.`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="domain-migration-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-sky-600" /> Domain Migration — {org?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Result screen */}
        {result ? (
          <div className="space-y-4" data-testid="migration-result">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-semibold text-emerald-800">
                  {result.type === "migrate"
                    ? `Migration complete — ${result.data.migrated_count} account(s) updated`
                    : `Rollback complete — ${result.data.reverted_count} account(s) reverted`}
                </p>
                {result.type === "migrate" && (
                  <p className="text-sm text-emerald-700">
                    Notification emails: {result.data.notification_emails?.sent ?? 0} sent
                    {result.data.skipped_conflicts?.length ? `, ${result.data.skipped_conflicts.length} conflict(s) skipped` : ""}
                  </p>
                )}
              </div>
            </div>
            <Button onClick={onClose} className="w-full" data-testid="migration-done-btn">Done</Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Existing migration / rollback panel */}
            {latest && (
              <div className={`p-4 rounded-lg border ${latest.rollback_available ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="migration-history-panel">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">
                      Last migration: @{latest.current_domain} <ArrowRight className="inline w-3 h-3" /> @{latest.new_domain}
                      {latest.rolled_back && <Badge className="ml-2 bg-slate-200 text-slate-700">Rolled back</Badge>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(latest.timestamp).toLocaleString()} · {latest.migrated_count} account(s)
                    </p>
                  </div>
                </div>
                {latest.rollback_available && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={rollbackConfirmed} onChange={(e) => setRollbackConfirmed(e.target.checked)} data-testid="rollback-confirm-checkbox" className="mt-0.5" />
                      <span>Confirm rollback: revert all @{latest.new_domain} accounts back to @{latest.current_domain}.</span>
                    </label>
                    <Button onClick={rollback} disabled={busy || !rollbackConfirmed} variant="outline" className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-100" data-testid="rollback-execute-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                      Rollback Migration
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Inputs */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Current domain</label>
                <Input value={currentDomain} onChange={(e) => { setCurrentDomain(e.target.value); setPreview(null); setConfirmed(false); }} placeholder="divihn.co" data-testid="current-domain-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">New domain</label>
                <Input value={newDomain} onChange={(e) => { setNewDomain(e.target.value); setPreview(null); setConfirmed(false); }} placeholder="divihn.com" data-testid="new-domain-input" />
              </div>
            </div>

            <Button onClick={loadPreview} disabled={loading} variant="outline" className="w-full" data-testid="load-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preview affected users
            </Button>

            {/* Preview table */}
            {preview && (
              <div className="space-y-3" data-testid="migration-preview">
                {preview.conflict_count > 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{preview.conflict_count} user(s) will be SKIPPED — the target email already exists.</span>
                  </div>
                )}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">User</th>
                        <th className="text-left px-3 py-2">Current Email</th>
                        <th className="text-left px-3 py-2">New Email</th>
                        <th className="text-left px-3 py-2">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.affected_users.map((u) => (
                        <tr key={u.user_id} className="border-t border-slate-100" data-testid={`affected-row-${u.user_id}`}>
                          <td className="px-3 py-2 font-medium text-slate-800">{u.name}</td>
                          <td className="px-3 py-2 text-slate-500">{u.current_email}</td>
                          <td className="px-3 py-2 text-sky-700 font-medium">{u.new_email}</td>
                          <td className="px-3 py-2"><Badge variant="outline">{roleLabel(u.role)}</Badge></td>
                        </tr>
                      ))}
                      {preview.conflict_users.map((u) => (
                        <tr key={`c-${u.user_id}`} className="border-t border-slate-100 bg-red-50/40">
                          <td className="px-3 py-2 font-medium text-slate-800">{u.name}</td>
                          <td className="px-3 py-2 text-slate-500">{u.current_email}</td>
                          <td className="px-3 py-2 text-red-600 line-through">{u.new_email}</td>
                          <td className="px-3 py-2"><Badge className="bg-red-100 text-red-700">Conflict</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {preview.affected_count > 0 && (
                  <>
                    <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="migration-confirm-checkbox" className="mt-1" />
                      <span>{confirmText}</span>
                    </label>
                    <Button onClick={execute} disabled={busy || !confirmed} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="execute-migration-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Execute Migration ({preview.affected_count} account{preview.affected_count !== 1 ? "s" : ""})
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
